import { useEffect, useRef, useState } from "react";

/**
 * Run an async fetcher whose result is rendered as a live preview while the
 * user edits some input. Caps concurrent calls at one in-flight, with a
 * trailing-edge re-run if the input changed during the fetch — so a slider
 * drag fires once at the start and once at the end, not once per pixel.
 * Pass a stable JSON-serialisable `key` (the inputs you'd otherwise debounce);
 * `enabled` gates the effect off when the panel isn't visible.
 */
export function useThrottledPreview<T>(
  key: string,
  fetcher: () => Promise<T>,
  enabled: boolean,
  fallback: T,
): T {
  const [value, setValue] = useState<T>(fallback);
  const latestFetcher = useRef(fetcher);
  latestFetcher.current = fetcher;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await latestFetcher.current();
        if (!cancelled) setValue(result);
      } catch {
        if (!cancelled) setValue(fallback);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return value;
}
